import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-use-analytics-api/react-use-analytics-api/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>useAuthorize</h1>
    <h2 {...{
      "id": "description"
    }}>{`Description`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`useAuthorize`}</inlineCode>{` hook can be used to authorize the user via the `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useAnalyticsApi"
      }}>{`Google Analytics Embed API`}</a>{` in several ways:`}</p>
    <ul>
      <li parentName="ul">{`Render an authorize button using a `}<a parentName="li" {...{
          "href": "https://justinmahar.github.io/react-analytics-charts/google-oauth-client-id"
        }}>{`Client ID`}</a></li>
      <li parentName="ul">{`Authorize with an `}<a parentName="li" {...{
          "href": "https://ga-dev-tools.appspot.com/embed-api/server-side-authorization/"
        }}>{`access token`}</a></li>
    </ul>
    <p>{`The user must be authorized before the API can query for things like chart data.`}</p>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useAuthorize } from "react-use-analytics-api";
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`useAuthorize`}</inlineCode>{` hook returns an `}<inlineCode parentName="p">{`authorize`}</inlineCode>{` function that calls `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth"
      }}><inlineCode parentName="a">{`gapi.auth.authorize`}</inlineCode></a>{` using the provided `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth-options"
      }}>{`auth options`}</a>{`.`}</p>
    <h3 {...{
      "id": "using-a-client-id"
    }}>{`Using A `}<a parentName="h3" {...{
        "href": "https://justinmahar.github.io/react-analytics-charts/google-oauth-client-id"
      }}>{`Client ID`}</a></h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const authorize = useAuthorize(gapi, {
  clientId:
    "1234567890123-abc123def456ghi789jkl012mno345pq.apps.googleusercontent.com",
  container: "authorize-container-id",
});
`}</code></pre>
    <p>{`Call `}<inlineCode parentName="p">{`authorize`}</inlineCode>{` to render an authorize button (when signed out) and sign-in information (when signed in) into the container with that ID. The button can be used to authorize the user.`}</p>
    <p>{`If the user is already authorized and reloads the page, they will be signed in immediately when calling `}<inlineCode parentName="p">{`authorize`}</inlineCode>{`.`}</p>
    <p>{`The contents of the container are not managed by React, so be sure to leave it empty and not to do anything to trigger a render on that container.`}</p>
    <h4 {...{
      "id": "limitations"
    }}>{`Limitations`}</h4>
    <p><strong parentName="p">{`Google's API doesn't render the authorize button more than once per page load.`}</strong>{` Be sure to take this into consideration.`}</p>
    <p>{`A novel approach is used in the `}<a parentName="p" {...{
        "href": "#example"
      }}>{`example below`}</a>{` to detect whether the container is empty and prompt the user to refresh the page.`}</p>
    <h3 {...{
      "id": "using-an-access-token"
    }}>{`Using An `}<a parentName="h3" {...{
        "href": "https://ga-dev-tools.appspot.com/embed-api/server-side-authorization/"
      }}>{`Access Token`}</a></h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const authorize = useAuthorize(gapi, {
  serverAuth: {
    access_token: "ACCESS_TOKEN_GOES_HERE",
  },
});
`}</code></pre>
    <p>{`Call `}<inlineCode parentName="p">{`authorize`}</inlineCode>{` to authorize using the access token provided.`}</p>
    <h2 {...{
      "id": "signature"
    }}>{`Signature`}</h2>
    <p><inlineCode parentName="p">{`useAuthorize( gapi: GoogleAnalyticsEmbedAPI | undefined, options: AuthorizeOptions, onSignIn?: () => void ) => (() => void)`}</inlineCode></p>
    <h3 {...{
      "id": "arguments"
    }}>{`Arguments`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Argument`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`gapi`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`GoogleAnalyticsEmbedAPI`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "/react-use-analytics-api/useAnalyticsApi"
            }}>{`Google Analytics Embed API`}</a>{`. When `}<inlineCode parentName="td">{`undefined`}</inlineCode>{`, calling `}<inlineCode parentName="td">{`authorize`}</inlineCode>{` does nothing.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`options`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth-options"
            }}><inlineCode parentName="a">{`AuthorizeOptions`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth-options"
            }}>{`options`}</a>{` for the authorization.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`onSignIn`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`() => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Called on a successful sign-in. You can use this to do things like change the view on auth success.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "return"
    }}>{`Return`}</h3>
    <p>{`The hook returns a function, `}<inlineCode parentName="p">{`authorize`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`authorize() => void
`}</code></pre>
    <p>{`When `}<inlineCode parentName="p">{`gapi`}</inlineCode>{` is not `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, this function calls `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth"
      }}><inlineCode parentName="a">{`gapi.auth.authorize`}</inlineCode></a>{` using the provided `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#auth-options"
      }}>{`auth options`}</a>{`.`}</p>
    <h2 {...{
      "id": "example"
    }}>{`Example`}</h2>
    <p>{`The following example demonstrates use of the `}<inlineCode parentName="p">{`useAuthorize`}</inlineCode>{` hook with a `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-analytics-charts/google-oauth-client-id"
      }}>{`Client ID`}</a>{` to sign a user into Google Analytics.`}</p>
    <p>{`Due to an `}<a parentName="p" {...{
        "href": "#limitations"
      }}>{`API limitation`}</a>{`, the authorize button can only be rendered to the page once per page load. This means once the container `}<inlineCode parentName="p">{`div`}</inlineCode>{` is destroyed and re-rendered by React, the authorize button will no longer appear.`}</p>
    <p>{`To see this happening, use the sidebar to navigate to another page on this site (which doesn't trigger a full page reload), then come back to this one. The API will no longer render the authorize button in the example below.`}</p>
    <p>{`To take this into consideration, we use a `}<inlineCode parentName="p">{`ref`}</inlineCode>{` to check if the authorize container has any contents after `}<inlineCode parentName="p">{`authorize`}</inlineCode>{` is called. If it's empty when it shouldn't be, we show a prompt to refresh the page. You can replace this with a button that performs a refresh, if you'd like.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from "react";
import {
  useAnalyticsApi,
  useAuthorize,
  useSignOut,
} from "react-use-analytics-api";

export function UseAuthorizeExample(props) {
  const { ready, gapi, authorized, error } = useAnalyticsApi();
  const authorize = useAuthorize(gapi, {
    clientId,
    container: "authorize-container-id",
  });
  const signOut = useSignOut(gapi);

  // Workaround for API limitation
  const [authorizeCalled, setAuthorizeCalled] = React.useState(false);
  const authDiv = React.useRef(null);
  const hasAuthElements =
    authDiv.current && authDiv?.current?.children?.length > 0;

  React.useEffect(() => {
    if (ready && !error && !authorizeCalled) {
      authorize();
      setAuthorizeCalled(true);
    }
  }, [authorize, authorizeCalled, error, ready]);

  return (
    <div>
      {!error &&
        (ready && !!gapi ? (
          <div>
            {authorized && (
              <div>
                ☀️ Logged into Google Analytics!{" "}
                <button onClick={() => signOut()}>Sign Out</button>
              </div>
            )}
            {!authorized && <div>🔐 Not logged into Google Analytics</div>}
            <div id="authorize-container-id" ref={authDiv} />
            {!authorized && authorizeCalled && !hasAuthElements && (
              <div>🔄 Refresh the page to access analytics.</div>
            )}
          </div>
        ) : (
          <div>⌛ Loading...</div>
        ))}
      {error && <div>{error.toString()}</div>}
    </div>
  );
}
`}</code></pre>
    <h2 {...{
      "id": "manually-synchronizing-hooks"
    }}>{`Manually Synchronizing Hooks`}</h2>
    <p>{`If you authorize or sign out manually outside of this library (and React), you will want to synchronize any hooks you have instantiated so they're aware of your new authorization status.`}</p>
    <p>{`To do this, you can access the emitter and singleton used by the hooks in this library:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { apiStateEmitter, apiSingleton } from "react-use-analytics-api";
`}</code></pre>
    <p>{`Then, when your auth state changes, you can manually update the hooks via the `}<inlineCode parentName="p">{`authorized`}</inlineCode>{` event:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`apiSingleton.authorized = isAuthorized;
apiStateEmitter.emit("authorized", isAuthorized);
`}</code></pre>
    <p>{`This will update the authorized state for all existing hooks as well as any new hooks instantiated thereafter.`}</p>
    <p>{`You don't need to worry about this if you use the `}<inlineCode parentName="p">{`useAuthorize`}</inlineCode>{` and `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useSignOut"
      }}><inlineCode parentName="a">{`useSignOut`}</inlineCode></a>{` hooks, since they do this for you automatically.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      